import React, { useState, useEffect, lazy } from 'react'
import { Router, Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { ResetCSS, Text } from 'toonswap-uikit'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import useEagerConnect from 'hooks/useEagerConnect'
import { useReferralBind } from 'hooks/profileApi'
import { useFetchPriceList, useFetchProfile, useFetchPublicData } from 'state/hooks'
import { useWeb3React } from '@web3-react/core'
import Hunt from 'views/Hunt'
import useTheme from 'hooks/useTheme'
import useGetDocumentTitlePrice from './hooks/useGetDocumentTitlePrice'
import GlobalStyle from './style/Global'
import Menu from './components/Menu'
import SuspenseWithChunkError from './components/SuspenseWithChunkError'
import PageLoader from './components/PageLoader'
import EasterEgg from './components/EasterEgg'
import Pools from './views/Pools'
import history from './routerHistory'
// import SnowLayer from './components/SnowLayer1'

import 'assets/fonts/fonts.css'

// import { RedirectDuplicateTokenIds, RedirectOldAddLiquidityPathStructure } from './views/AddLiquidity/redirects'
// import { RedirectOldRemoveLiquidityPathStructure } from './views/RemoveLiquidity/redirects'

// Route-based code splitting
// Only pool is included in the main bundle because of it's the most visited page
const Home = lazy(() => import('./views/Home'))
const Disperse = lazy(() => import('./views/Disperse'))
const Bridge = lazy(() => import('./views/Bridge'))
const Farms = lazy(() => import('./views/Farms'))
const Vaults = lazy(() => import('./views/Vaults'))
const Lottery = lazy(() => import('./views/Lottery'))
const Ifos = lazy(() => import('./views/Ifos'))
const IfoDetailsPage = lazy(() => import('./views/Ifos/IfoDetailsPage'))
const NotFound = lazy(() => import('./views/NotFound'))
const Collectibles = lazy(() => import('./views/Collectibles'))
const Teams = lazy(() => import('./views/Teams'))
const Team = lazy(() => import('./views/Teams/Team'))
const Profile = lazy(() => import('./views/Profile'))
const TradingComp = lazy(() => import('./views/TradingComp/TradingComp'))
const Marketplace = lazy(() => import('./views/Marketplace'))
const CollectionDetail = lazy(() => import('./views/Marketplace/CollectionDetail'))
const NftDetail = lazy(() => import('./views/Marketplace/NftDetail'))
const MyNfts = lazy(() => import('./views/Marketplace/MyNfts'))
const TokenCreation = lazy(() => import('./views/TokenCreation'))
const NFT = lazy(() => import('./views/NFT'))
// exchange routes
const AddLiquidity = lazy(() => import('./views/AddLiquidity'))
const RemoveLiquidity = lazy(() => import('./views/RemoveLiquidity'))
const Pool = lazy(() => import('./views/Pool'))
const PoolFinder = lazy(() => import('./views/PoolFinder'))
const Swap = lazy(() => import('./views/Swap'))
const Zapin = lazy(() => import('./views/Zapin'))
const RedirectOldRemoveLiquidityPathStructure = lazy(() => import('./views/RemoveLiquidity/redirects'))
const RedirectOldAddLiquidityPathStructure = lazy(() => import('./views/AddLiquidity/redirects'))
const RedirectDuplicateTokenIds = lazy(() => import('./views/AddLiquidity/redirects'))
const SquidSwap = lazy(() => import('./views/SquidSwap'))
const Leaderboard = lazy(() => import('./views/Leaderboard'))
const PointsCenter = lazy(() => import('./views/PointsCenter'))

// const MAIN_CONTENT_MAX_WIDTH_PX = 1270

// This config is required for number formating
BigNumber.config({
  EXPONENTIAL_AT: 1000,
  DECIMAL_PLACES: 80,
})

const PresaleAlert = styled.div`
  background: #e5e8ec;
  font-weight: bold;
  padding: 21px 36px;
  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`

// CSS background images per page; '/images/back.png' is the frame drawn over the picture backgrounds
const getMainContentBackground = (pathname: string): string => {
  if (pathname.startsWith('/farms')) return "url('/images/back.png'), url('/images/bg_farm.png')"
  if (pathname.startsWith('/staking')) return 'linear-gradient(245.6deg, #97dbfa -33.45%, #ffffff 89.86%)'
  return "url('/images/back.png'), url('/images/bg_home.webp')"
}

const MainContentWidth = styled.div<{ $background: string }>`
  width: 100%;
  min-height: 100vh;
  margin-left: auto;
  margin-right: auto;
  background-image: ${({ $background }) => $background};
  background-position:
    center center,
    center bottom;
  background-repeat: no-repeat, no-repeat;
  background-size:
    100% 100%,
    102% auto;
`

const MainContentWidthWithRouteBg: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation()
  return <MainContentWidth $background={getMainContentBackground(pathname)}>{children}</MainContentWidth>
}

const AppWrapper = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  overflow-x: hidden;
`

const BodyWrapper = styled.div<any>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 32px 16px;
  align-items: center;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1;
  justify-content: center;
  background: transparent;
`

const Marginer = styled.div`
  margin-top: 5rem;
`

/** Redirect from / to /swap while preserving ?ref= and other query params so getRefFromUrl() still works. */
const RedirectToSwap: React.FC = () => {
  const location = useLocation()
  return <Redirect to={{ pathname: '/swap', search: location.search, hash: location.hash }} />
}

const App: React.FC = () => {
  const { account, chainId, connector } = useWeb3React()
  const { isDark } = useTheme()
  // Monkey patch warn() because of web3 flood
  // To be removed when web3 1.3.5 is released
  useEffect(() => {
    console.warn = () => null
  }, [])

  useEagerConnect()
  useFetchPublicData()
  useFetchProfile()
  useFetchPriceList()
  useGetDocumentTitlePrice()
  useReferralBind(account)

  if (!window.location.href.includes('bridge') && account && chainId.toString() !== process.env.REACT_APP_CHAIN_ID) {
    if (connector.deactivate) {
      connector.deactivate()
    }
    if (connector) {
      connector.resetState()
    }
  }

  const [presaleAlertEnabled, setPresaleAlertEnabled] = useState(false)
  useEffect(() => {
    const currentTimestamp = Math.floor(new Date().getTime() / 1000)
    if (currentTimestamp < 1698840000) {
      setPresaleAlertEnabled(true)
      setTimeout(() => {
        setPresaleAlertEnabled(false)
      }, 10000)
    }
  }, [])

  return (
    <Router history={history}>
      <ResetCSS />
      <GlobalStyle />
      <MainContentWidthWithRouteBg>
        <Menu>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            {/* <SnowLayer /> */}
            {presaleAlertEnabled && (
              <PresaleAlert>
                <Text color="#182149" fontSize="16px">
                  <a href="/toon-pad" style={{ color: '#eaae25' }}>
                    Toon Pad
                  </a>
                  .
                </Text>
              </PresaleAlert>
            )}
            <Switch>
              <Route path="/" exact>
                <RedirectToSwap />
              </Route>
              <Route path="/home">
                <Home />
              </Route>
              <Route path="/farms">
                <Farms />
              </Route>
              <Route path="/vaults">
                <Vaults />
              </Route>
              <Route path="/staking">
                <Pools />
              </Route>
              <Route path="/hunt">
                <Hunt />
              </Route>
              <Route path="/lottery">
                <Lottery />
              </Route>
              <Route path="/ido">
                <Redirect to="/toon-pad" />
              </Route>
              <Route path="/toon-pad/details/:address">
                <IfoDetailsPage />
              </Route>
              <Route path="/toon-pad">
                <Ifos />
              </Route>
              <Route path="/leaderboard">
                <Leaderboard />
              </Route>
              <Route path="/points-center">
                <PointsCenter />
              </Route>
              <Route path="/disperse">
                <Disperse />
              </Route>
              <Route path="/bridge">
                <Bridge />
              </Route>
              <Route path="/marketplace/:alias/:tokenId">
                <NftDetail />
              </Route>
              <Route path="/marketplace/:alias">
                <CollectionDetail />
              </Route>
              <Route path="/marketplace">
                <Marketplace />
              </Route>
              <Route path="/my-nfts">
                <MyNfts />
              </Route>
              <Route path="/collectibles">
                <Collectibles />
              </Route>
              <Route exact path="/teams">
                <Teams />
              </Route>
              <Route path="/teams/:id">
                <Team />
              </Route>
              <Route path="/profile">
                <Profile />
              </Route>
              {/* Redirect */}
              <Route path="/syrup">
                <Redirect to="/staking" />
              </Route>
              <Route path="/nft">
                <Redirect to="/collectibles" />
              </Route>
              <Route path="/campaigns/0">
                <TradingComp id={0} />
              </Route>
              <Route path="/token-creation">
                <TokenCreation />
              </Route>
              <Route path="/kyudo-nft">
                <NFT />
              </Route>
              {/* Exchange + catch-all so /swap, /pool, etc. render here instead of 404 */}
              <Route>
                <AppWrapper>
                  <BodyWrapper isDark={isDark}>
                    <Switch>
                      <Route path="/swap">
                        <Swap />
                      </Route>
                      {/* <Route path="/swap">
                    {(() => {
                      const redirectUrl = process.env.REACT_APP_SWAP_REDIRECT_URL || 'https://robin.toonswap.finance/'
                      const RedirectComponent = () => {
                        useEffect(() => {
                          window.location.href = redirectUrl
                        }, [])
                        return null
                      }
                      return <RedirectComponent />
                    })()}
                  </Route> */}
                      <Route path="/pool">
                        <Pool />
                      </Route>
                      <Route path="/cross-swap">
                        <SquidSwap />
                      </Route>
                      <Route path="/zap">
                        <Zapin />
                      </Route>
                      <Route path="/find">
                        <PoolFinder />
                      </Route>
                      <Route exact path="/add" component={AddLiquidity} />
                      <Route exact strict path="/remove/:currencyIdA/:currencyIdB" component={RemoveLiquidity} />

                      {/* Redirection: These old routes are still used in the code base */}
                      <Route exact path="/add/:currencyIdA" component={RedirectOldAddLiquidityPathStructure} />
                      <Route exact path="/add/:currencyIdA/:currencyIdB" component={RedirectDuplicateTokenIds} />
                      <Route exact strict path="/remove/:tokens" component={RedirectOldRemoveLiquidityPathStructure} />

                      {/* 404 */}
                      <Route>
                        <NotFound />
                      </Route>
                    </Switch>
                    <Marginer />
                  </BodyWrapper>
                </AppWrapper>
              </Route>
            </Switch>
          </SuspenseWithChunkError>
        </Menu>
      </MainContentWidthWithRouteBg>
      <EasterEgg iterations={2} />
    </Router>
  )
}

export default React.memo(App)
